/**
 * The keyboard model the dense surfaces share (#748): arrow keys move focus
 * between the items of one list, radio group, or grid of fields, so a reader
 * walks a register or a column instead of tabbing through every control.
 *
 * `roving` (the default) keeps exactly one item in the Tab order: the item the
 * reader last focused, or `activeIndex` (the checked radio, the current row)
 * until they move, and again whenever `activeIndex` changes. `roving: false`
 * leaves every item in the Tab order and adds only the arrow moves, the
 * spreadsheet convention for a grid of fields.
 *
 * The owning surface keeps its own acts. Buttons open on Enter and Space
 * natively; a link item opens on Space here as well, so every list item takes
 * the same two keys.
 */
import { useCallback, useRef, useState, type FocusEvent, type KeyboardEvent } from "react";

/**
 * `vertical` and `horizontal` are lists (with Home and End); `both` is the
 * radio pattern, where every arrow moves; `grid` is rows of `columns` items,
 * Up and Down moving a row and Left and Right moving within it.
 */
export type RovingOrientation = "vertical" | "horizontal" | "both" | "grid";

export interface RovingLayout {
  count: number;
  orientation: RovingOrientation;
  /** Items per row when `orientation` is `grid`. */
  columns?: number;
  /** Wrap past either end (the radio pattern). Lists and grids stop. */
  loop?: boolean;
}

/**
 * The index a key moves focus to from `index`. `null` means the key is not a
 * move in this layout; returning `index` itself means it is a move that has
 * reached an edge, which the caller still consumes so the page does not scroll.
 */
export function rovingTarget(key: string, index: number, layout: RovingLayout): number | null {
  const { count, orientation, columns = 1, loop = false } = layout;
  if (count <= 0) return null;
  const last = count - 1;
  const step = (delta: number) => {
    const next = index + delta;
    if (next >= 0 && next <= last) return next;
    return loop ? (next + count) % count : index;
  };
  const list = orientation === "vertical" || orientation === "horizontal";
  if (list && key === "Home") return 0;
  if (list && key === "End") return last;
  if (orientation === "grid") {
    const width = Math.max(1, columns);
    const column = index % width;
    if (key === "ArrowUp") return index - width >= 0 ? index - width : index;
    if (key === "ArrowDown") return index + width <= last ? index + width : index;
    if (key === "ArrowLeft") return column > 0 ? index - 1 : index;
    if (key === "ArrowRight") return column < width - 1 && index < last ? index + 1 : index;
    return null;
  }
  const backward = orientation === "vertical" ? ["ArrowUp"] : orientation === "horizontal" ? ["ArrowLeft"] : ["ArrowUp", "ArrowLeft"];
  const forward = orientation === "vertical" ? ["ArrowDown"] : orientation === "horizontal" ? ["ArrowRight"] : ["ArrowDown", "ArrowRight"];
  if (backward.includes(key)) return step(-1);
  if (forward.includes(key)) return step(1);
  return null;
}

/**
 * Left and Right leave a text field in a grid only from its edge: an empty
 * field, the caret at the start (Left) or end (Right), or the whole value
 * selected, which is how a field reads once focus arrives from a neighbour.
 * Anywhere else the keys move the caret, as they always do in a field.
 */
export function caretAtEdge(input: HTMLInputElement, key: "ArrowLeft" | "ArrowRight"): boolean {
  const length = input.value.length;
  const start = input.selectionStart ?? 0;
  const end = input.selectionEnd ?? length;
  if (length === 0 || (start === 0 && end === length)) return true;
  return key === "ArrowLeft" ? start === 0 && end === 0 : start === length && end === length;
}

/**
 * Keep a focused element clear of its table's sticky header and sticky first
 * column. Focus scrolling brings an element into the scroller's box, which can
 * still leave it underneath a pinned row or column; this finishes the job so
 * the cell's row and column labels both stay visible.
 */
export function revealClearOfSticky(element: HTMLElement): void {
  const scroller = element.closest<HTMLElement>('[data-slot="table-container"]');
  const cell = element.closest<HTMLElement>("td, th");
  if (!scroller || !cell) return;
  const box = scroller.getBoundingClientRect();
  const rect = cell.getBoundingClientRect();
  const header = scroller.querySelector<HTMLElement>("thead");
  const pinned = cell.parentElement?.firstElementChild;
  const headerHeight = header && getComputedStyle(header.querySelector("th") ?? header).position === "sticky" ? header.offsetHeight : 0;
  const pinnedWidth = pinned instanceof HTMLElement && pinned !== cell && getComputedStyle(pinned).position === "sticky" ? pinned.offsetWidth : 0;
  const top = box.top + scroller.clientTop + headerHeight;
  const bottom = box.top + scroller.clientTop + scroller.clientHeight;
  const left = box.left + scroller.clientLeft + pinnedWidth;
  const right = box.left + scroller.clientLeft + scroller.clientWidth;
  if (rect.top < top) scroller.scrollTop -= top - rect.top;
  else if (rect.bottom > bottom) scroller.scrollTop += rect.bottom - bottom;
  if (rect.left < left) scroller.scrollLeft -= left - rect.left;
  else if (rect.right > right) scroller.scrollLeft += rect.right - right;
}

export interface RovingFocusOptions<T extends HTMLElement> extends RovingLayout {
  /** The item that holds the tab stop until the reader moves. */
  activeIndex?: number;
  /** `false` keeps every item in the Tab order (a grid of fields). */
  roving?: boolean;
  /** Keep a focused item clear of its table's sticky header and column. */
  revealInTable?: boolean;
  /** After a key moves focus: the radio pattern selects here, a grid selects the field's text. */
  onMove?: (index: number, element: T) => void;
}

export interface RovingItemProps<T extends HTMLElement> {
  ref: (node: T | null) => void;
  tabIndex?: number;
  onFocus: (event: FocusEvent<T>) => void;
  onKeyDown: (event: KeyboardEvent<T>) => void;
}

export function useRovingFocus<T extends HTMLElement>({
  activeIndex,
  roving = true,
  revealInTable = false,
  onMove,
  ...layout
}: RovingFocusOptions<T>) {
  const items = useRef<(T | null)[]>([]);
  const [held, setHeld] = useState<{ index: number; active: number | undefined } | null>(null);
  const { count } = layout;
  const resting = activeIndex != null && activeIndex >= 0 && activeIndex < count ? activeIndex : 0;
  const stop = held && held.active === activeIndex && held.index < count ? held.index : resting;

  /** Focus an item by index; returns it, or `null` when there is none there. */
  const focus = useCallback((index: number): T | null => {
    const element = items.current[index] ?? null;
    element?.focus();
    return element;
  }, []);

  const itemProps = (index: number): RovingItemProps<T> => ({
    ref: (node) => {
      items.current[index] = node;
    },
    tabIndex: roving ? (index === stop ? 0 : -1) : undefined,
    onFocus: (event) => {
      if (roving && (held?.index !== index || held.active !== activeIndex)) setHeld({ index, active: activeIndex });
      if (revealInTable) revealClearOfSticky(event.currentTarget);
    },
    onKeyDown: (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (event.key === " " && event.currentTarget instanceof HTMLAnchorElement) {
        event.preventDefault();
        event.currentTarget.click();
        return;
      }
      const target = rovingTarget(event.key, index, layout);
      if (target == null) return;
      event.preventDefault();
      if (target === index) return;
      const element = focus(target);
      if (element) onMove?.(target, element);
    },
  });

  return { itemProps, focus, tabStop: stop };
}
